import { PhoneCommunication } from '@seen/core/models/communications/PhoneCommunication'
import type { ReactNode } from 'react'
import { useParams } from 'react-router'
import { Card } from '#src/components/core/Card.tsx'
import { Loader } from '#src/components/core/Loader.tsx'
import { ResourceCard } from '#src/components/core/ResourceCard.tsx'
import { ScrollAreaContent } from '#src/components/core/scroll-area/ScrollAreaContent.tsx'
import { DataDisplay } from '#src/components/display/DataDisplay.tsx'
import { DateTimeDisplay } from '#src/components/display/DateTimeDisplay.tsx'
import { PageLayout } from '#src/components/display/PageLayout.tsx'
import { PhoneCallTranscript } from '#src/components/phone-calls/PhoneCallTranscript.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { usePortalStore } from '#src/hooks/usePortalStore.ts'
import { useRead } from '#src/hooks/useRead.ts'

export function PhoneCallPage(): ReactNode {
  const { communicationId } = useParams()
  const { data: phoneCall, isLoading } = useRead(
    PhoneCommunication,
    communicationId ?? ''
  )

  const showAdminActions = usePortalStore((state) => state.showAdminActions)
  if (isLoading) {
    return <Loader />
  }
  if (!phoneCall) {
    return null
  }

  return (
    <PageLayout
      title="Phone Call Details"
      contentClassName="mx-auto w-full max-w-[calc(76ch+3rem)]"
    >
      <ScrollAreaContent className="gap-6 p-6">
        <Card className="space-y-4">
          <h3 className="text-lg font-semibold">Call Information</h3>
          <div className="grid grid-cols-3 gap-4">
            <DataDisplay
              label="Start Time"
              value={<DateTimeDisplay value={phoneCall.startTime} />}
            />
            {phoneCall.duration && (
              <DataDisplay label="Duration" value={phoneCall.duration} />
            )}
            <DataDisplay label="Caller" value={phoneCall.callerNumber} />
            <DataDisplay label="Receiver" value={phoneCall.receiverNumber} />
            <DataDisplay
              label="Direction"
              value={phoneCall.isOutbound ? 'Outbound' : 'Inbound'}
            />
            {phoneCall.sender && (
              <DataDisplay
                label="Sender"
                value={<ResourceCard value={phoneCall.sender} />}
              />
            )}
            {phoneCall.subject && (
              <DataDisplay
                label="Subject"
                value={<ResourceCard value={phoneCall.subject} />}
              />
            )}
            {phoneCall.partOf.length > 0 && (
              <DataDisplay
                label="Related To"
                value={<ResourceCard value={phoneCall.partOf[0]} />}
              />
            )}
            <DataDisplay
              label="Recording"
              value={phoneCall.isRecorded ? 'Yes' : 'No'}
            />
            {phoneCall.zoomCallLogId && (
              <DataDisplay
                label="Zoom Call Log ID"
                value={phoneCall.zoomCallLogId}
              />
            )}
            {showAdminActions && phoneCall.externalResourceJSON && (
              <DataDisplay
                label="JSON"
                value={
                  <pre>
                    {JSON.stringify(
                      JSON.parse(phoneCall.externalResourceJSON),
                      null,
                      2
                    )}
                  </pre>
                }
              />
            )}
          </div>
        </Card>

        {phoneCall.messageSummary && (
          <Card>
            <h3 className="text-lg font-semibold">Summary</h3>
            <RichText value={phoneCall.messageSummary} />
          </Card>
        )}

        {phoneCall.transcript.length > 0 && (
          <PhoneCallTranscript phoneCall={phoneCall} />
        )}
      </ScrollAreaContent>
    </PageLayout>
  )
}
